import { LAYERS } from '../lib/floating';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  CheckCheck,
  FileSignature,
  Gift,
  Loader2,
  Megaphone,
  Sparkles,
  TriangleAlert,
  UserPlus,
  Wrench,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { Notification } from '../lib/types';

/**
 * The bell.
 *
 * Everything addressed to you in one place — a contract signed, a problem
 * reported, a referral ready to approve — plus the release notes, which is how
 * anybody finds out what changed in the application without an email nobody
 * reads.
 *
 * Two decisions worth naming:
 *
 *  - **The badge polls; the list does not.** The count is one integer and the
 *    shell renders it on every screen, so it refreshes on a timer. The list is
 *    forty rows of body text and is fetched only when the panel is opened.
 *    Polling that to render one digit would be most of the traffic this
 *    application generates.
 *  - **Opening an item marks it read and navigates.** Not two actions. A tray
 *    where you have to dismiss things separately is a tray that stays full.
 */

const ICONS: Record<Notification['kind'], typeof Bell> = {
  RELEASE: Sparkles,
  CONTRACT: FileSignature,
  PROBLEM: TriangleAlert,
  LEAD: UserPlus,
  REFERRAL: Gift,
  TICKET: Wrench,
  SYSTEM: Megaphone,
};

/**
 * Colour carries the kind, and never carries it alone.
 *
 * Each row also shows an icon with a distinct silhouette and a word, so the
 * tray reads the same to somebody who cannot separate red from amber.
 */
const TONES: Record<Notification['kind'], string> = {
  RELEASE: 'text-primary-600',
  CONTRACT: 'text-secondary-600',
  PROBLEM: 'text-danger-600',
  LEAD: 'text-accent-600',
  REFERRAL: 'text-accent-600',
  TICKET: 'text-secondary-600',
  SYSTEM: 'text-muted-foreground',
};

const LABELS: Record<Notification['kind'], string> = {
  RELEASE: "What's new",
  CONTRACT: 'Contract',
  PROBLEM: 'Problem',
  LEAD: 'Lead',
  REFERRAL: 'Referral',
  TICKET: 'Ticket',
  SYSTEM: 'Notice',
};

/** "3 minutes ago" beats a timestamp for anything from the last day. */
function whenever(iso: string): string {
  const then = new Date(iso).getTime();
  const minutes = Math.round((Date.now() - then) / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 60 * 24) {
    const hours = Math.round(minutes / 60);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  return new Intl.DateTimeFormat('en-KE', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Africa/Nairobi',
  }).format(new Date(iso));
}

export function NotificationTray() {
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const count = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: api.unreadCount,
    refetchInterval: 60_000,
    // A tray that cannot reach the server should show nothing rather than a
    // stale number that makes somebody hunt for a message that is not there.
    retry: false,
  });

  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.notifications(40),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAll = useMutation({
    mutationFn: api.markAllNotificationsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Click outside and Escape both close it. A panel that only closes by
  // pressing the same button again is one people leave open by accident.
  useEffect(() => {
    if (!open) return;

    const onClick = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = count.data?.unread ?? 0;

  const openItem = (item: Notification) => {
    if (!item.read) markRead.mutate(item.id);
    setOpen(false);
    if (item.linkTo) navigate(item.linkTo);
  };

  return (
    <div className="relative" ref={panel}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative rounded-xl p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center
              rounded-full bg-danger-500 px-1 text-[10px] font-semibold text-white"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          // `LAYERS.flyout`, not a hand-picked z-50: that number used to collide
          // with dialogs, and whether the tray drew above one was luck. Flat, too
          // — `shadow-lg` predates the theme.
          style={{ zIndex: LAYERS.flyout }}
          className="absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden
            rounded-2xl border border-border bg-card"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="font-heading text-sm font-semibold">Notifications</h3>
            {unread > 0 && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:underline"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
              >
                <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {list.isPending && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
              </div>
            )}

            {list.isError && (
              <p className="px-4 py-8 text-center text-sm text-danger-700">
                Could not load your notifications.
              </p>
            )}

            {list.data?.notifications.length === 0 && (
              <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                Nothing here yet. This is where contracts, problems and updates arrive.
              </p>
            )}

            <ul className="divide-y divide-border">
              {list.data?.notifications.map((item) => {
                const Icon = ICONS[item.kind] ?? Bell;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => openItem(item)}
                      className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted
                        ${item.read ? '' : 'bg-primary-50/40'}`}
                    >
                      <Icon
                        className={`mt-0.5 h-4 w-4 shrink-0 ${TONES[item.kind] ?? ''}`}
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-baseline gap-x-2">
                          <span
                            className={`text-sm ${item.read ? 'font-medium' : 'font-semibold'}`}
                          >
                            {item.title}
                          </span>
                          {/* The word, not only the colour. */}
                          <span className="text-[11px] text-muted-foreground">
                            {LABELS[item.kind] ?? 'Notice'}
                          </span>
                        </p>
                        <p className="mt-0.5 line-clamp-2 whitespace-pre-line text-xs text-muted-foreground">
                          {item.body}
                        </p>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {whenever(item.createdAt)}
                          {item.broadcast ? ' · to everyone in your role' : ''}
                        </p>
                      </div>
                      {!item.read && (
                        <span
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500"
                          aria-label="Unread"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="border-t border-border px-4 py-2.5">
            <button
              type="button"
              className="text-xs font-medium text-primary-600 hover:underline"
              onClick={() => {
                setOpen(false);
                navigate('/whats-new');
              }}
            >
              See everything that has changed
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
